import { z } from "zod";
import {
  BooleanInputSchema,
  CheckboxGroupSchema,
  EmailInputSchema,
  FormFieldSchema,
  FormFieldType,
  NumberInputSchema,
  RadioGroupSchema,
  RangeInputSchema,
  TextInputSchema,
  UrlInputSchema,
} from "@app/components/forms/types/schema";
import { GradientType } from "../../../../../../packages/ui/src/components/ColorGenerator/GradientGenerator/types";

export const generateZodSchema = (
  formItems: FormFieldSchema[],
): z.ZodObject<any> => {
  const shape: z.ZodRawShape = {};

  if (!formItems || formItems.length === 0) {
    return z.object({});
  }

  for (const item of formItems) {
    if (item.type === FormFieldType.Group) {
      const groupSchema = generateZodSchema(item.schemas);
      Object.assign(shape, groupSchema.shape);
      continue;
    }

    if (item.type === FormFieldType.Separator) {
      continue;
    }

    const isRequired = item.behavior?.required ?? false;

    switch (item.type) {
      case FormFieldType.Text:
      case FormFieldType.Textarea:
      case FormFieldType.Select:
      case FormFieldType.Password:
        shape[item.id] = getTextSchema(item as TextInputSchema);
        break;
      case FormFieldType.Email:
        shape[item.id] = getEmailSchema(item);
        break;
      case FormFieldType.URL:
        shape[item.id] = getURLSchema(item);
        break;
      case FormFieldType.Number:
      case FormFieldType.Range:
        shape[item.id] = getNumberSchema(item);
        break;
      case FormFieldType.RadioGroup:
        shape[item.id] = getEnumSchema(item);
        break;
      case FormFieldType.Dropdown:
      case FormFieldType.CheckboxGroup:
        shape[item.id] = getArraySchema(item as CheckboxGroupSchema);
        break;
      case FormFieldType.Checkbox:
      case FormFieldType.Switch:
        shape[item.id] = getBooleanSchema(item as BooleanInputSchema);
        break;
      case FormFieldType.Color:
        shape[item.id] = z.object({
          type: z.enum([GradientType.Linear, GradientType.Radial, GradientType.Conic]),
          colorStops: z.array(z.object({
            color: z.string(),
            offset: z.number(),
          })),
          angle: z.number(),
        }, {
          required_error: `${item.label}은(는) 필수 항목입니다.`,
        });
        break;
      default:
        {
          const schema = z.any();

          if (!isRequired) {
            schema.optional();
          }

          shape[item.id] = schema;
        }
        break;
    }
  }

  return z.object(shape);
};

const getTextSchema = (item: TextInputSchema) => {
  const stringItem = item as TextInputSchema;

  if (stringItem.validationFunction !== undefined) {
    return z.string().refine(stringItem.validationFunction, {
      message: `${item.label}의 형식이 올바르지 않습니다.`,
    });
  }

  if (stringItem.pattern !== undefined) {
    return z.string().regex(stringItem.pattern, {
      message: `${item.label}의 형식이 올바르지 않습니다.`,
    });
  }

  let schema: any = z.string({
    required_error: `${item.label}은(는) 필수 항목입니다.`,
  });

  if (stringItem.minLength !== undefined) {
    schema = schema.refine(
      (value: string) => {
        if (
          stringItem.behavior?.required === false &&
          (value === undefined || value.trim() === "")
        ) {
          return true; // 값이 없으면 통과
        }
        return value.length >= stringItem.minLength!;
      },
      {
        message: `${item.label}은(는) 최소 ${stringItem.minLength}자 이상이어야 합니다.`,
      },
    );
  } else {
    // 필수 항목인 경우
    if(stringItem.behavior?.required) {
      schema = schema.trim().min(1, {
        message: `${item.label}은(는) 필수 항목입니다.`,
      });
    }
  }

  if (stringItem.maxLength !== undefined) {
    schema = schema.refine(
      (value: string) => {
        if (
          stringItem.behavior?.required === false &&
          (value === undefined || value.trim() === "")
        )
          return true; // 값이 없으면 통과
        return value.length <= stringItem.maxLength!;
      },
      {
        message: `${item.label}은(는) 최대 ${stringItem.maxLength}자 이하이어야 합니다.`,
      },
    );
  }

  if (!stringItem.behavior?.required) {
    return schema.optional();
  }

  return schema;
};

const getEmailSchema = (item: EmailInputSchema) => {
  let schema = z
    .string({
      required_error: `${item.label}은(는) 필수 항목입니다.`,
    })
    .email({ message: `${item.label}의 형식이 올바르지 않습니다.` });

  if (!item.behavior?.required) {
    return schema.optional();
  }

  return schema;
};

const getURLSchema = (item: UrlInputSchema) => {
  let schema: any = z.string({
    required_error: `${item.label}은(는) 필수 항목입니다.`,
  });

  schema = schema.refine(
    (value: string) => {
      if (
        item.behavior?.required === false &&
        (value === undefined || value.trim() === "")
      )
        return true;
      return RegExp(
        /^(https?:\/\/)?([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(:\d+)?(\/[^\s]*)?$/g,
      ).test(value);
    },
    {
      message: `${item.label}의 형식이 올바르지 않습니다.`,
    },
  );

  if (!item.behavior?.required) {
    return schema.optional();
  }

  return schema;
};

const getNumberSchema = (item: NumberInputSchema | RangeInputSchema) => {
  let schema: any = z.string({
    // invalid_type_error: `${item.label}은(는) 숫자여야 합니다.`,
    required_error: `${item.label}은(는) 필수 항목입니다.`,
  });

  if (item.min !== undefined) {
    schema = schema.refine(
      (value: string) => {
        if (
          item.behavior?.required === false &&
          (value === undefined || value === null)
        )
          return true;
        return parseFloat(value) >= item.min!;
      },
      {
        message: `${item.label}은(는) 최소 ${item.min} 이상이어야 합니다.`,
      },
    );
  }

  if (item.max !== undefined) {
    schema = schema.refine(
      (value: string) => {
        if (
          item.behavior?.required === false &&
          (value === undefined || value === null)
        )
          return true;
        return parseFloat(value) <= item.max!;
      },
      {
        message: `${item.label}은(는) 최대 ${item.max} 이하이어야 합니다.`,
      },
    );
  }

  // schema = schema.min(item.min, { message: `${item.label}은(는) 최소 ${item.min} 이상이어야 합니다.` });
  // schema = schema.max(item.max, { message: `${item.label}은(는) 최대 ${item.max} 이하이어야 합니다.` });

  if (!item.behavior?.required) {
    return schema.optional();
  }

  return schema;
};

const getBooleanSchema = (item: BooleanInputSchema) => {
  let schema = z.boolean({
    invalid_type_error: `${item.label}의 값이 올바르지 않습니다.`,
  });

  if (!item.behavior?.required) {
    return schema.optional();
  }

  return schema;
};

const getArraySchema = (item: CheckboxGroupSchema) => {
  let schema: z.ZodType;
  const optionValues = item.options.map(
    (option: { value: any }) => option.value,
  );

  schema = z.array(z.enum(optionValues as [string, ...string[]]), {
    required_error: `${item.label}은(는) 필수 항목입니다.`,
    invalid_type_error: `${item.label}은(는) 필수 항목입니다.`,
  });

  schema = schema.refine(
    (value: string[]) => {
      if (item.behavior?.required && value.length === 0) return false;
      return value.every((v) => optionValues.includes(v));
    },
    {
      message: `${item.label}은(는) 필수 항목입니다.`,
    },
  );

  if (!item.behavior?.required) {
    schema = schema.optional();
  }

  return schema;
};

const getEnumSchema = (item: RadioGroupSchema) => {
  let schema: z.ZodType;
  const optionValues = item.options.map(
    (option: { value: any }) => option.value,
  );

  schema = z.enum(optionValues as [string, ...string[]], {
    // required_error: `${item.label}은(는) 필수 항목입니다.`,
    // invalid_type_error: `${item.label}은(는) 필수 항목입니다.`,
    errorMap: (issue, ctx) => ({
      message: `${item.label}은(는) 필수 항목입니다.`,
    }),
  });

  schema = schema.refine(
    (value: string) => {
      if (item.behavior?.required && value === "") return false;
      return optionValues.includes(value);
    },
    {
      message: `${item.label}은(는) 필수 항목입니다.`,
    },
  );

  if (!item.behavior?.required) {
    schema = schema.optional();
  }

  return schema;
};
